/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import Header from '../../../components/Header';
import Sidebar from '../../../components/Sidebar';
import AdvancedTable from '../../../components/AdvancedTable';
import { refreshicon } from '../../../components/imagepath';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import dayjs from 'dayjs';

// Actions
import {
  ActionGetProgrammeBooking,
  ActionUpdateProgrammeSlot,
} from '../../../store/Actions/ServiceAction';

// Utils
import { matchPermissions } from '../../../utils/Permission';
import { PERMISSIONS } from '../../../constants';

// Custom Hooks
import { useProgrammeBookingColumns } from './hooks/useProgrammeBookingColumns';
import { useProgrammeBookingFilters } from './hooks/useProgrammeBookingFilters';

const ProgramSlotBooking = () => {
  const dispatch = useDispatch();
  const { loader } = useSelector(state => state?.Service);
  const { userDetails } = useSelector(state => state?.auth);
  const location = useLocation();
  const matchPermissionsForUser = matchPermissions(userDetails);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Permissions
  const canView = matchPermissionsForUser(PERMISSIONS.BOOKINGS?.PROGRAM_BOOKING) !== false;
  const canUpdate = matchPermissionsForUser('programme-slot.update') !== false;
  const canExport = matchPermissionsForUser(PERMISSIONS.BOOKINGS?.EXPORT) !== false;

  // Status update handler
  const handleStatusUpdate = useCallback(
    (recordId, newStatus) => {
      dispatch(
        ActionUpdateProgrammeSlot(
          {
            Query: recordId,
            obj: { status: newStatus },
          },
          () => setRefreshTrigger(prev => prev + 1)
        )
      );
    },
    [dispatch]
  );

  // Fetch data function for AdvancedTable
  const fetchData = useCallback(
    async (params) => {
      const {
        page = 1,
        perPage = 10,
        sortField,
        sortOrder,
        search,
        customerName,
        programmeName,
        status = 'Upcoming',
        dateRange_start,
        dateRange_end,
      } = params;

      // Build query string
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      // queryParams.append('limit', perPage); // Optional depending on API support

      if (sortField) queryParams.append('sortOn', sortField);
      if (sortOrder) queryParams.append('sort', sortOrder);
      if (search) queryParams.append('search', search);
      if (customerName) queryParams.append('customerName', customerName);
      if (programmeName) queryParams.append('programmeName', programmeName);
      if (status) queryParams.append('status', status);

      // Date range filters
      if (dateRange_start) {
        queryParams.append('startDate', dayjs(dateRange_start).format('YYYY-MM-DD'));
      }
      if (dateRange_end) {
        queryParams.append('endDate', dayjs(dateRange_end).format('YYYY-MM-DD'));
      }

      const queryStringVal = `?${queryParams.toString()}`;

      // Return a Promise that resolves when the API call completes
      return new Promise((resolve) => {
        dispatch(
          ActionGetProgrammeBooking({ Query: queryStringVal }, (responseData) => {
            if (responseData) {
              // Handle response format which could be wrapped in an array or direct
              const result = Array.isArray(responseData) ? responseData[0] : responseData;
              resolve({
                data: result?.docs || [],
                total: result?.totalDocs || 0,
              });
            } else {
              resolve({
                data: [],
                total: 0,
              });
            }
          })
        );
      });
    },
    [dispatch, refreshTrigger]
  );

  // Get configuration from hooks
  const columns = useProgrammeBookingColumns({
    canUpdate,
    onStatusUpdate: handleStatusUpdate,
  });
  const filterConfig = useProgrammeBookingFilters();

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/programme-booking?page=1">Programme</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Programme-Booking</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Advanced Table */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire">
                <div className="card-body">
                  {/* Page Header with Action Buttons */}
                  <div className="page-table-header mb-2">
                    <div className="row align-items-center justify-content-between">
                      <div className="col-12 col-md-8">
                        <div className="doctor-table-blk">
                          <h3>Programme Booking</h3>
                          <div className="doctor-search-blk">
                            <div className="add-group">
                              {/* Refresh Button */}
                              <Link
                                to="/programme-booking?page=1"
                                className="btn btn-primary doctor-refresh ms-2"
                              >
                                <img src={refreshicon} alt="refresh" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {canView ? (
                    <AdvancedTable
                      resource="programme-booking"
                      columns={columns}
                      fetchData={fetchData}
                      loading={loader}
                      enableSearch={false}
                      enableFilters={true}
                      enableRefresh={true}
                      enableExport={canExport}
                      enableColumnManager={true}
                      enableHorizontalScrollFeature={true}
                      enableToolBar={true}
                      filterConfig={filterConfig}
                      defaultPageSize={10}
                      defaultFilters={{ status: 'Upcoming' }}
                      persistState={true}
                      tableId="programme-bookings-v2"
                      stickyHeader={true}
                      striped={true}
                      highlightOnHover={true}
                      density="comfortable"
                      key={`programme-table-${refreshTrigger}`}
                    />
                  ) : (
                    <div className="permission-denied-message" style={{ textAlign: 'center', padding: '50px' }}>
                      <FeatherIcon icon="lock" size={48} color="#ccc" />
                      <h3 className="mt-3">Access Denied</h3>
                      <p>You do not have permission to view programme bookings.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgramSlotBooking;
